import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { type Person } from 'types';
import {
  getPeopleCustomSurveyAdministratorEligibleQuery,
  INPUT_TYPES,
} from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';

interface AdministeredEntity {
  administrators?: Person[];
}

interface ManageAdministratorsSaveData {
  administrator_ids: number[];
}

interface Props<T extends AdministeredEntity> {
  isOpen: boolean;
  toggle: () => void;
  onSave: (data: ManageAdministratorsSaveData) => void;
  entity: T | null;
}

interface ManageAdministratorsFormData {
  administrators: Person[];
}

const EMPTY_FORM: ManageAdministratorsFormData = { administrators: [] };

const ManageAdministratorsModal = <T extends AdministeredEntity>({
  isOpen,
  toggle,
  onSave,
  entity,
}: Props<T>) => {
  const { formatMessage } = useIntl();

  const [formObj, setFormObj] =
    useState<ManageAdministratorsFormData>(EMPTY_FORM);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setFormObj({ administrators: entity?.administrators ?? [] });
  }, [isOpen, entity]);

  const inputs = useMemo(
    () => [
      {
        type: INPUT_TYPES.PEOPLE_EDITOR,
        name: 'administrators',
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.manage_administrators.administrators_label',
          defaultMessage: 'Administrators',
        }),
        placeholder: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.manage_administrators.administrators_placeholder',
          defaultMessage: 'Add administrators',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.manage_administrators.administrators_helper_text',
          defaultMessage:
            'Administrators have the same permissions as the owner: they can edit, archive, delete, and manage this list.',
        }),
        allowSelf: true,
        elasticsearchOptions: {
          getQuery: getPeopleCustomSurveyAdministratorEligibleQuery,
        },
      },
    ],
    [formatMessage]
  );

  const handleChange = useCallback((newObj: ManageAdministratorsFormData) => {
    setFormObj(newObj);
  }, []);

  const transformObjectBeforeSubmit = useCallback(
    (data: ManageAdministratorsFormData): ManageAdministratorsSaveData => ({
      administrator_ids: (data.administrators || []).map((p: Person) => p.id),
    }),
    []
  );

  const originalIds = useMemo(
    () => new Set((entity?.administrators ?? []).map((p) => p.id)),
    [entity]
  );

  const isUnchanged = useMemo(() => {
    const currentIds = (formObj.administrators || []).map((p) => p.id);
    if (currentIds.length !== originalIds.size) return false;
    return currentIds.every((id) => originalIds.has(id));
  }, [formObj.administrators, originalIds]);

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      title={formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.manage_administrators.title',
        defaultMessage: 'Manage administrators',
      })}
      object={formObj}
      inputs={inputs}
      onChange={handleChange}
      transformObjectBeforeSubmit={transformObjectBeforeSubmit}
      submitText={formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.manage_administrators.save',
        defaultMessage: 'Save',
      })}
      disableUnsavedChangesPrompt={true}
      triggerCallbackBeforeClose={true}
      disabled={isUnchanged}
      callback={onSave}
    />
  );
};

export default React.memo(
  ManageAdministratorsModal
) as typeof ManageAdministratorsModal;
